/**
 * Navigation, create surfaces, sidebar and list-page interaction events.
 *
 * One slice of the `ANALYTICS_EVENTS` SSOT — see `../analytics.ts`, which
 * spreads every slice back into the single import surface call sites use.
 * The event NAME STRINGS are a PostHog dashboard contract: add and retire keys
 * here, never rename a value in place (see `rules/analytics.md`).
 */

export const NAVIGATION_EVENTS = {
  // Navigation
  PAGE_VIEWED: 'Page Viewed',
  PAGE_TIME: 'Page Time',

  // Create surfaces
  // Fired when a user picks a type in the Create modal's step 1 (the entry
  // point that opened it is in `surface`) or from the Protocols page's New
  // protocol button.
  // Properties: create_type ∈ voice | photo | import | note | protocol |
  // spreadsheet | equipment | collection; surface; parent_entity_type; and,
  // from the Create modal only, step ('choose') and destination_source
  // ('context' | 'entry_point' | 'picker' | 'new_collection'). Fired on click; the
  // downstream creation events (Artifact / Collection / Protocol / Equipment
  // Created) still fire on their own success.
  // Renamed 2026-07-25 from 'Sidebar Create Menu Item Clicked', a name that
  // described only the sidebar's menu.
  CREATE_MENU_ITEM_CLICKED: 'Create Menu Item Clicked',
  // The one Create modal opened. Properties:
  // surface (the entry point), preselected_type (a type the entry point
  // opened it at, or null), destination_type ('workspace' | 'project').
  CREATE_MODAL_OPENED: 'Create Modal Opened',
  // Closed without creating. Properties: surface, step ('choose' | 'details' |
  // 'picker'), type (the type whose step was open, or null). Paired with
  // OPENED, answers "do people open one type and abandon it for another?".
  CREATE_MODAL_DISMISSED: 'Create Modal Dismissed',
  // The modal's destination changed from where it opened. Properties:
  // surface, destination_source ('picker' | 'new_collection'),
  // destination_type.
  CREATE_DESTINATION_CHANGED: 'Create Destination Changed',
  // Expanding the links/tags/attachments pill on a detail page. Properties:
  // panel ('links' | 'tags' | 'attachments'), count. The pills are the primary
  // discovery affordance for relationships and, since attachments collapsed
  // behind a pill too, for a note's files — so opens are the engagement signal
  // for that surface.
  ENTITY_META_PANEL_OPENED: 'Entity Meta Panel Opened',
  // A finished row's name in the background-tasks (upload) tray was clicked
  // through to the artifact it produced. Properties: artifact_type, intent.
  UPLOAD_TRAY_ITEM_OPENED: 'Upload Tray Item Opened',
  // Dictation <-> meeting. Properties: mode, source. Fires on BOTH directions —
  // VOICE_NOTE_DIARIZATION_ENABLED only ever fired on 'meeting', so a
  // meeting->dictation->meeting sequence logged two enables, zero reverts, and
  // nothing recorded the mode actually uploaded.
  TRANSCRIPT_MODE_CHANGED: 'Transcript Mode Changed',
  // Per-user pins (sidebar redesign 2026-07). Properties: entity_type.
  // Pin drag-reorder (when it ships) reuses SIDEBAR_ITEM_REORDERED with a
  // `section: 'pinned'` property — do not mint a separate event.
  // The SIDEBAR_ITEM_* names outlive the sidebar itself, and the strings are
  // a dashboard contract. The workspace drawer's kebab events (Renamed, Link
  // Copied, Copied, Moved, Deleted) were retired with the drawer in 2026-10:
  // the folders UI tracks those actions as WORKSPACE_ITEM_RENAMED,
  // WORKSPACE_ITEMS_MOVED and WORKSPACE_ITEMS_CLIPBOARD_COPIED; a trash from
  // the action bar fires BULK_OPERATION_COMPLETED (useBulkTrash). ('Sidebar
  // Library Expanded' was retired with the Library dock in 2026-07, for the
  // same reason.)
  SIDEBAR_ITEM_PINNED: 'Sidebar Item Pinned',
  SIDEBAR_ITEM_UNPINNED: 'Sidebar Item Unpinned',
  SIDEBAR_ITEM_REORDERED: 'Sidebar Item Reordered',
  // The workspace sidebar shown from hidden (collapsed, or behind Bower). The
  // name predates the sidebar, which replaced the workspace drawer; kept so
  // the series continues. No properties since 2026-10 (the drawer's `tab` went
  // with it).
  WORKSPACE_DRAWER_OPENED: 'Workspace Drawer Opened',

  // Interactions
  COMMAND_PALETTE_OPENED: 'Command Palette Opened',
  /** Empty collection → Ask Bird tile, prefilled with the collection name. */
  COLLECTION_EMPTY_ASK_BIRD: 'Collection Empty Ask Bird',
  COMMAND_PALETTE_ASK_BIRD: 'Command Palette Ask Bird',
  COMMAND_PALETTE_FILTER_VISIBILITY_TOGGLED: 'Command Palette Filter Visibility Toggled',
  SEARCH_PERFORMED: 'Search Performed',
  SEARCH_RESULT_CLICKED: 'Search Result Clicked',
  /** "Show all results in Workspace" in the palette: opens the full ranked list. */
  SEARCH_SHOW_ALL_CLICKED: 'Search Show All Clicked',
  /** Retry on a palette search that failed (429 / 5xx), not on an empty answer. */
  SEARCH_RETRIED: 'Search Retried',
  CHAT_MESSAGE_SENT: 'Chat Message Sent',
  FEEDBACK_SUBMITTED: 'Feedback Submitted',
  CHAT_FEEDBACK_SUBMITTED: 'Chat Feedback Submitted',
  /**
   * A table Bird rendered was taken out of the thread — copied or downloaded.
   * One event for both, discriminated by `format`, rather than two near-
   * duplicates. Properties: format ('html_tsv' a rich copy, 'tsv' a plain-text
   * copy, 'csv' a download), row_count, column_count.
   * This is the adoption signal for the in-thread table block.
   */
  CHAT_TABLE_EXPORTED: 'Chat Table Exported',
  /** Provenance panel opened on an answer. Properties: source_count. */
  CHAT_SOURCES_OPENED: 'Chat Sources Opened',
  WHATS_NEW_OPENED: 'Whats New Opened',

  /**
   * Code block interactions, shared across every surface that renders code —
   * chat, the note editor, and the rendered preview for an uploaded code file.
   * `surface` ('chat' | 'note' | 'attachment') discriminates them rather than
   * three near-duplicate event names, matching CHAT_TABLE_EXPORTED's `format`.
   *
   * The `language` distribution is the empirical answer to "which languages do
   * researchers actually bring us" — the question that motivated this work.
   *
   * PII: `language` and `line_count` are metadata and safe. Code CONTENT and
   * FILE NAMES are UGC and must never appear in properties (rules/analytics.md).
   */
  CODE_BLOCK_COPIED: 'Code Block Copied',
  CODE_BLOCK_DOWNLOADED: 'Code Block Downloaded',
  /** A collapsed long block was expanded. Properties: language, line_count, surface. */
  CODE_BLOCK_EXPANDED: 'Code Block Expanded',
  /** An uploaded code file rendered in the viewer. Properties: language, size_bytes. */
  CODE_ATTACHMENT_PREVIEWED: 'Code Attachment Previewed',
  /** An uploaded Markdown file rendered in the viewer. Properties: size_bytes. */
  MARKDOWN_ATTACHMENT_PREVIEWED: 'Markdown Attachment Previewed',
  /** An uploaded HTML file rendered in the viewer's sandboxed frame. Properties: size_bytes. */
  HTML_ATTACHMENT_PREVIEWED: 'HTML Attachment Previewed',
  /** Language chosen on a note code block. Properties: language. */
  NOTE_CODE_LANGUAGE_SET: 'Note Code Language Set',

  // List pages (Trash, Protocols) — server-driven search / sort / filter /
  // pagination. LIST_SEARCHED and LIST_SORTED fire from the shared
  // useServerListControls hook, LIST_FILTERED from each page's filter
  // controls, LIST_NEXT_PAGE_LOADED from useProtocolLibraryList. Properties
  // use the snake_case convention per rules/analytics.md.
  LIST_SEARCHED: 'List Searched',
  LIST_SORTED: 'List Sorted',
  LIST_FILTERED: 'List Filtered',
  LIST_NEXT_PAGE_LOADED: 'List Next Page Loaded',

  // The "in <folder>" link on an item page opened from a section list
  // (folders UI). Properties: artifact_type, origin ('protocols'),
  // target ('folder'|'workspace'). No names: they are UGC.
  ARTIFACT_LOCATION_LINK_CLICKED: 'Artifact Location Link Clicked',
} as const;
